import * as vp from 'vopay';

import { FunctionComponent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TransactionsContext from './TransactionsContext';

import { DialogueHandler, TransactionDialogueHandlers } from '../types';

const { ConfirmationDialogue } = vp;

type TransactionsDialogueProps = {
  handleDialogue: DialogueHandler<TransactionDialogueHandlers>;
};

type FC = FunctionComponent<TransactionsDialogueProps>;

const TransactionsDialogue: FC = ({ handleDialogue }: TransactionsDialogueProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionsDialogue' });

  const {
    showTransactionsSearchErrorAlert,
    showTransactionsRefundErrorAlert,
    showTransactionsRefundSuccessAlert,
    transactionsSearchError,
    transactionRefundError,
  } = useContext(TransactionsContext);

  return useMemo(
    () => (
      <div>
        {/* Transaction Search Error */}

        <ConfirmationDialogue
          isVisible={showTransactionsSearchErrorAlert}
          status="error"
          title={t('SearchError.title', { defaultValue: 'Error while trying to fetch transactions.' })}
          subTitle={t('SearchError.subTitle', {
            defaultValue: 'Please address the following issue(s): {{transactionsSearchError}}',
            transactionsSearchError,
          })}
          confirm={{
            label: t('backToTransactions', { defaultValue: 'Back to Transactions' }),
            action: () => handleDialogue({ type: 'search', state: 'fail', hasNavigation: false }),
          }}
        />

        {/* Transaction Refund Error */}

        <ConfirmationDialogue
          isVisible={showTransactionsRefundErrorAlert}
          title={t('RefundError.title', { defaultValue: 'Error! The refund was not successful.' })}
          status="error"
          subTitle={t('RefundError.subTitle', {
            defaultValue: 'Transaction {{transactionRefundError}}',
            transactionRefundError,
          })}
          confirm={{
            label: t('backToAccounts', { defaultValue: 'Back to Accounts' }),
            action: () => handleDialogue({ type: 'refund', state: 'fail', hasNavigation: true }),
          }}
          cancel={{
            label: t('stayHere', { defaultValue: 'Stay Here' }),
            action: () => handleDialogue({ type: 'refund', state: 'fail', hasNavigation: false }),
          }}
        />

        {/* Transaction Refund Success */}

        <ConfirmationDialogue
          isVisible={showTransactionsRefundSuccessAlert}
          title={t('RefundSuccess.title', { defaultValue: 'Success! The transaction has been refunded.' })}
          status="success"
          subTitle={t('RefundSuccess.subTitle', {
            defaultValue: 'Navigate back to accounts or refresh your transactions.',
          })}
          confirm={{
            label: t('backToAccounts', { defaultValue: 'Back to Accounts' }),
            action: () => handleDialogue({ type: 'refund', state: 'success', hasNavigation: true }),
          }}
          cancel={{
            label: t('stayHere', { defaultValue: 'Stay Here' }),
            action: () => handleDialogue({ type: 'refund', state: 'success', action: 'refresh', hasNavigation: false }),
          }}
        />
      </div>
    ),
    [
      showTransactionsSearchErrorAlert,
      showTransactionsRefundErrorAlert,
      showTransactionsRefundSuccessAlert,
      transactionsSearchError,
      transactionRefundError,
      handleDialogue,
      t,
    ],
  );
};

export default TransactionsDialogue;
